import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import PostProductList from './PostProductList';
import {
  StyledModal,
  WithLoading,
  FormButton,
  CustomNotification,
} from 'common/components';
import { Row, Col, message } from 'antd';

import { useGetProductFullView, useGlobalModal } from 'hooks';
import {
  useGetWalmartProductSchema,
  useGetWalmartProductData,
  useSaveWalmartFormValueWhenExit,
} from './queries';

import ModalConfirmPostToWalmartKrogger from './ModalConfirmPostToWalmartKrogger';
import {
  clearAllPostToWalmartState,
  selectPostToWalmartFormErrors,
  selectPostToWalmartFormValues,
  setPostToWalmartFormValues,
} from 'pages/branded-products/components/post-to-walmart-krogger/slice';
import { useCheckAllowToPostGDSN, useValidateWalmartFormData } from './hooks';
import ValidateButton from './common/ValidateButton';
import { getErrorView, hasNoBrickCodeError } from './utils';

import PublicationProductToWalmartRecipients from 'pages/branded-products/components/publication-product/PublicationProductToWalmartRecipients';
import PostToGdsnSuggestionModal from './common/PostToGdsnSuggestionModal';
import WalmartConfirmUpdateModal from './WalmartConfirmUpdateModal';
import { cleanInvalidData } from './post-product-form/utils';

import './style.less';
import { isArray } from 'lodash';

const PostToModal = (props) => {
  const {
    isFullView = false,
    isPostToModalOpen,
    togglePostToModal,
    selectedProducts,
  } = props;

  const dispatch = useDispatch();
  const { id: productId } = useParams();

  const [destroyModalError, setDestroyModalError] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [needToRepublishList, setNeedToRepublishList] = useState([]);

  const [
    isPostGdsnSuggestionModalVisible,
    setIsPostGdsnSuggestionModalVisible,
  ] = useState(false);

  const walmartFormValues = useSelector(selectPostToWalmartFormValues);
  const walmartFormErrors = useSelector(selectPostToWalmartFormErrors);

  const { productFull } = useGetProductFullView({ productId });

  const { MODAL_NAMES, checkModalVisible, closeModal } = useGlobalModal();

  const productPostIdList = isFullView
    ? [productFull?.productId]
    : selectedProducts?.map((productItem) => +productItem?.id);

  const { handleValidateWalmartProductData, isValidatingForm } =
    useValidateWalmartFormData();

  const {
    refetch: refetchWalmartProductSchema,
    removeCache: removeCachedWalmartProductSchema,
    fetchStatus: fetchStatusWalmartProductionSchema,
    data: walmartProductSchema,
  } = useGetWalmartProductSchema({
    productIds: productPostIdList,
    enabled: Boolean(isPostToModalOpen),
    onError: (error) => {
      setDestroyModalError(error);
    },
  });

  const {
    data: initialWalmartProductDataList,
    refetch: refetchWalmartProductData,
    removeCache: removeCacheWalmartProductData,
    fetchStatus: fetchStatusWalmartProductDataList,
  } = useGetWalmartProductData({
    productIds: productPostIdList,
    isGetLatestProductData: true,
    enabled: Boolean(isPostToModalOpen) && Boolean(walmartProductSchema),
    onError: (error) => {
      setDestroyModalError(error);
    },
  });

  const initWalmartProductData = (initWalmartFormValue) => {
    const formattedInitFormValue = cleanInvalidData(initWalmartFormValue);

    dispatch(setPostToWalmartFormValues(formattedInitFormValue));

    handleValidateWalmartProductData(formattedInitFormValue);
    const tempNeedToRepublishList = isArray(initWalmartFormValue)
      ? initWalmartFormValue?.map((val) => ({
          needToRepublish: val?.needToRepublish,
          productId: val?.productId,
        }))
      : [];
    setNeedToRepublishList(tempNeedToRepublishList);
  };

  useEffect(() => {
    if (
      initialWalmartProductDataList &&
      isPostToModalOpen &&
      Boolean(walmartProductSchema)
    ) {
      initWalmartProductData(initialWalmartProductDataList);
    }
  }, [
    JSON.stringify(initialWalmartProductDataList),
    isPostToModalOpen,
    walmartProductSchema,
  ]);

  const isAllowToPublishGdsn = useCheckAllowToPostGDSN({
    isFullView,
    productFull,
    selectedProducts,
  });

  const refetchSchemaAndWalmartProductData = () => {
    refetchWalmartProductSchema();
    refetchWalmartProductData();
  };

  const isModalConfirmPostToWalmartVisible = checkModalVisible(
    MODAL_NAMES.CONFIRM_POST_TO_WALMART_LIST
  );

  const handleCloseModalConfirmPostTopWalmart = () => {
    closeModal(MODAL_NAMES.CONFIRM_POST_TO_WALMART_LIST);
  };

  const { saveWalmartFormMutation } = useSaveWalmartFormValueWhenExit();

  const handleClosePostToWalmartModal = useCallback(() => {
    togglePostToModal();
    removeCacheWalmartProductData();
    removeCachedWalmartProductSchema();
    dispatch(clearAllPostToWalmartState());
    setDestroyModalError(null);
  }, []);

  const handleClosePostToWalmartModalWithWarningNotice = () => {
    setConfirmModal(true);
  };

  const onCancel = () => {
    setConfirmModal(false);
  };

  const onExitWithoutSaving = () => {
    handleClosePostToWalmartModal();
    setConfirmModal(false);
  };

  const onSaveAndExit = async () => {
    try {
      const productMatchingData = walmartFormValues?.map((val) => ({
        ...val,
        needToRepublish:
          isArray(needToRepublishList) &&
          needToRepublishList?.find(
            (item) => val?.productId === item?.productId
          )?.needToRepublish,
      }));
      const response = await saveWalmartFormMutation.mutateAsync({
        productMatchingData,
      });
      const { isSuccess, message } = response || {};
      if (isSuccess) {
        CustomNotification.success('Save Walmart Data successfully!');
        onExitWithoutSaving();
      } else {
        CustomNotification.error(message || 'Save Walmart Data failed!');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onSuccessPostToWalmartConfirmModal = async () => {
    try {
      const formattedFormValues = cleanInvalidData(walmartFormValues);
      const productMatchingData = formattedFormValues?.map((val) => ({
        ...val,
        needToRepublish:
          isArray(needToRepublishList) &&
          needToRepublishList?.find(
            (item) => val?.productId === item?.productId
          )?.needToRepublish,
      }));
      const response = await saveWalmartFormMutation.mutateAsync({
        productMatchingData,
      });
      const { isSuccess, message } = response || {};
      if (isSuccess) {
        handleCloseModalConfirmPostTopWalmart();
        handleClosePostToWalmartModal();
      } else {
        CustomNotification.error(message || 'Save Walmart Data failed!');
      }
    } catch (error) {
      console.log(error);
    }

    if (isAllowToPublishGdsn) {
      setIsPostGdsnSuggestionModalVisible(true);
    }
  };

  const isChecking =
    fetchStatusWalmartProductionSchema === 'fetching' ||
    fetchStatusWalmartProductDataList === 'fetching' ||
    isValidatingForm;

  const errorView = useMemo(() => {
    return getErrorView({
      error: destroyModalError,
      handleClosePostToWalmartModal,
      isPostToModalOpen,
    });
  }, [destroyModalError, handleClosePostToWalmartModal, isPostToModalOpen]);

  return (
    <>
      <StyledModal
        title='Post product to Walmart'
        visible={isPostToModalOpen}
        width={750}
        bodyStyle={{
          minHeight: 'min(60vh, 100px)',
          maxHeight: 'min(65vh, 800px)',
        }}
        cancelText='Close'
        okText={'Re-validate'}
        onCancel={() => handleClosePostToWalmartModalWithWarningNotice()}
        onOk={() => handleValidateWalmartProductData()}
        maskClosable={false}
        okButtonProps={{ disabled: false }}
        footer={
          <PostToWalmartModalFooter
            isChecking={isChecking}
            refetchSchemaAndWalmartProductData={
              refetchSchemaAndWalmartProductData
            }
          />
        }
        {...(errorView?.modalOverrideConfig || {})}
      >
        {errorView ? (
          errorView.ErrorViewRender
        ) : (
          <>
            <WithLoading loading={isChecking} tip='checking...'>
              <PostProductList
                selectedProducts={selectedProducts}
                isFullView={isFullView}
                needToRepublishList={needToRepublishList}
                setNeedToRepublishList={setNeedToRepublishList}
              />
            </WithLoading>
            <ModalConfirmPostToWalmartKrogger
              visible={isModalConfirmPostToWalmartVisible}
              validationList={walmartFormErrors}
              walmartFormValues={walmartFormValues}
              needToRepublishList={needToRepublishList}
              onSuccess={onSuccessPostToWalmartConfirmModal}
              onCancel={handleCloseModalConfirmPostTopWalmart}
            />
          </>
        )}
      </StyledModal>

      <PostToGdsnSuggestionModal
        visible={isPostGdsnSuggestionModalVisible}
        onCancel={() => setIsPostGdsnSuggestionModalVisible(false)}
        productCount={selectedProducts?.length}
        targetType='walmart'
      />
      <PublicationProductToWalmartRecipients
        isFullView={isFullView}
        productId={productId}
      />
      <WalmartConfirmUpdateModal
        visible={confirmModal}
        onCancel={onCancel}
        onSaveAndExit={onSaveAndExit}
        onExitWithoutSaving={onExitWithoutSaving}
      />
    </>
  );
};

const PostToWalmartModalFooter = ({
  isChecking,
  refetchSchemaAndWalmartProductData,
}) => {
  const { handleValidateWalmartProductData } = useValidateWalmartFormData();

  const { MODAL_NAMES, openModal } = useGlobalModal();

  const walmartFormErrors = useSelector(selectPostToWalmartFormErrors);

  const formHasError = walmartFormErrors?.some(
    (walmartFormError) => walmartFormError?.dataErrors?.length > 0
  );

  const formHasNoBrickCodeError = hasNoBrickCodeError({
    walmartFormErrors,
  });

  const onClickValidate = async () => {
    if (formHasNoBrickCodeError) {
      refetchSchemaAndWalmartProductData();
    } else {
      await handleValidateWalmartProductData();
    }
  };

  const disabledPostToWalmart =
    formHasError || !walmartFormErrors || isChecking;

  return (
    <Row>
      <Col>
        <ValidateButton
          onClick={() => onClickValidate()}
          hasError={formHasError}
          disabled={!walmartFormErrors}
          loading={isChecking}
        />
      </Col>
      <Col flex='auto'></Col>
      <Col>
        <FormButton
          style={{ borderRadius: 0 }}
          text='Post product(s) to Walmart'
          onClick={() => openModal(MODAL_NAMES.CONFIRM_POST_TO_WALMART_LIST)}
          disabled={disabledPostToWalmart}
        ></FormButton>
      </Col>
    </Row>
  );
};

export default PostToModal;
